import React, { useState, useEffect } from 'react';
import HeroCarousel from './HeroCarousel';
import PersonalizedRecommendations from './PersonalizedRecommendations';
import FeaturedSpotlight from './FeaturedSpotlight';
import AllCategoriesSection from './AllCategoriesSection';
import { fetchHomepageDataCached, fetchCategoriesWithSubcategories } from '../services/homepageService';
import { useNavigate } from 'react-router-dom';

const Homepage = () => {
  const [homepageData, setHomepageData] = useState(null);
  const [categoriesData, setCategoriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoriesError, setCategoriesError] = useState(null);
  const navigate = useNavigate();

  // Load diverse homepage data using new service
  useEffect(() => {
    const loadHomepageData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch diverse products from multiple categories
        const data = await fetchHomepageDataCached();
        setHomepageData(data);

        // Log diversity metrics for monitoring
        console.log('📊 Homepage Diversity Metrics:', {
          totalProducts: data.metadata.totalProducts,
          sources: data.metadata.sourcesUsed,
          loadTime: `${data.metadata.loadTimeMs}ms`
        });

        // Transform the already-fetched categories for AllCategoriesSection
        // This reuses the categories instead of making another API call
        if (data.categories && data.categories.length > 0) {
          setCategoriesLoading(true);
          try {
            const transformedCategories = await fetchCategoriesWithSubcategories(data.categories);
            setCategoriesData(transformedCategories);

            console.log('📂 Transformed categories for discovery section:', {
              totalCategories: transformedCategories.length,
              totalSubcategories: transformedCategories.reduce((sum, cat) => sum + cat.subcategories.length, 0)
            });
          } catch (catErr) {
            console.error('Error transforming categories:', catErr);
            setCategoriesError('Failed to load categories');
          } finally {
            setCategoriesLoading(false);
          }
        }
      } catch (err) {
        console.error('Error loading homepage data:', err);
        setError('Failed to load homepage data');
      } finally {
        setLoading(false);
      }
    };

    loadHomepageData();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-blue-600 mx-auto"></div>
            <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-blue-200 opacity-20"></div>
          </div>
          <p className="text-gray-700 font-medium mt-4 text-lg">Loading Your Marketplace</p>
          <p className="text-gray-500 text-sm mt-2">Curating products from multiple categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">

      {/* Main Content Container */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Hero Carousel - Major Promotions */}
        <section className="mb-12">
          <HeroCarousel />
        </section>

        {/* Amazon-style Category Discovery - ALL CATEGORIES */}
        <section className="mb-12">
          <AllCategoriesSection
            categories={categoriesData}
            loading={categoriesLoading}
            error={categoriesError}
          />
        </section>

        {/* Trending Today - Now with REAL variety from multiple categories */}
        {homepageData?.trending && homepageData.trending.length > 0 && (
          <section className="mb-12">
            <PersonalizedRecommendations
              title="Trending Today"
              subtitle={`Popular picks across ${homepageData.metadata.sourcesUsed} categories`}
              products={homepageData.trending}
            />
          </section>
        )}

        {/* Featured Spotlight + Benefits - Now data-driven */}
        {/* <section className="mb-12">
          <FeaturedSpotlight />
        </section> */}

        {/* Best Sellers - Now based on ratings and diversity */}
        {homepageData?.bestSellers && homepageData.bestSellers.length > 0 && (
          <section className="mb-12">
            <PersonalizedRecommendations
              title="Best Sellers"
              subtitle="Top-rated products our customers love"
              products={homepageData.bestSellers}
            />
          </section>
        )}

        {/* New Arrivals - Fresh products across categories */}
        {homepageData?.newArrivals && homepageData.newArrivals.length > 0 && (
          <section className="mb-12">
            <PersonalizedRecommendations
              title="New Arrivals"
              subtitle="Recently added to our marketplace"
              products={homepageData.newArrivals}
            />
          </section>
        )}

        

        {/* Error State */}
        {error && (
          <section className="mb-12 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700 font-semibold">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            >
              Retry
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

export default Homepage;